import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  authService,
  offerService,
  partnerService,
} from '../lib/api-services';
import { AuthUser, handleApiError } from '../lib/api-client';
import { AddAdditionalAddOfferForm, AddOfferForm } from '@/shared/partnerschema';



// ==================== AUTH HOOKS ====================

export function useAuthMe(options?: UseQueryOptions<AuthUser, Error>) {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authService.me,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      queryClient.clear();
      window.location.href = "https://bprm-ai-portal.workfinity.com/login";
    },
    onError: (error) => {
      console.error('Logout error:', handleApiError(error));
    },
  });
}

// GET PARTNERS

export function useGetPartner(
  pageNumber: string,
  pageSize?: string,
  partnerName = "",
  partnerType = "",
  partnerSubType = ""
) {
  return useQuery({
    queryKey: [
      "partners",
      pageNumber,
      pageSize,
      partnerName,
      partnerType,
      partnerSubType,
    ],
    queryFn: () =>
      partnerService.getPartners(
        pageNumber,
        pageSize,
        partnerName,
        partnerType,
        partnerSubType
      ),
  });
}

export function useAddPartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: partnerService.addPartner,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["partners"],
      });
    },
    onError: (error) => {
      console.error(handleApiError(error));
    },
  });
}

export function useEditPartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: partnerService.editPartner,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["partners"],
      });
    },
    onError: (error) => {
      console.error(handleApiError(error));
    },
  });
}

export function useDeletePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id }: { Id: number | undefined }) => partnerService.deletePartner(Id),
    onError: (error) => {
      console.error(handleApiError(error));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["partners"],
      });
    },
  });
}

// OFFERS

export function useGetOffers(
  partnerId: number,
  page: number,
  pageSize: number
) {
  return useQuery({
    queryKey: ["offers", partnerId, page, pageSize],
    queryFn: () =>
      offerService.getOffers(
        partnerId,
        page,
        pageSize
      ),
    enabled: !!partnerId,
  });
}

export function useAddOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      partnerId,
      data,
    }: {
      partnerId: number;
      data: AddOfferForm;
    }) => offerService.addOffer(partnerId, data),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["offers"],
      });
    },
  });
}

export function useEditOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: offerService.editOffer,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["offers"],
      });
    },
  });
}

export function useToggleOfferStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: offerService.toggleOfferStatus,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["offers"],
      });
    },
  });
}

export function useGetAdditionalOffers(
  offerId: number,
  pageNumber: number,
  pageSize: number
) {
  return useQuery({
    queryKey: [
      "additionalOffers",
      offerId,
      pageNumber,
      pageSize,
    ],
    queryFn: () =>
      offerService.getAdditionalOffers(
        offerId,
        pageNumber,
        pageSize
      ),
    enabled: !!offerId,
  });
}

export function useAddAdditionalOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      offerId,
      data,
    }: {
      offerId: number;
      data: AddAdditionalAddOfferForm;
    }) => offerService.addAdditionalOffer(offerId, data),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["additionalOffers"],
      });
    },
  });
}

export function useEditAdditionalOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      offerId,
      additionalOfferId,
      data,
    }: {
      offerId: number;
      additionalOfferId: number;
      data: AddAdditionalAddOfferForm;
    }) =>
      offerService.editAdditionalOffer(
        offerId,
        additionalOfferId,
        data
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["additionalOffers"],
      });
    },
  });
}

export function useGetRules(
  page = 1,
  pageSize = 10,
  offerId?: number | null
) {
  return useQuery({
    queryKey: ["rules", page, pageSize, offerId],
    queryFn: () =>
      offerService.getRules(
        page,
        pageSize,
        offerId
      ),
  });
}

export function useGetRuleCompose(ruleId: number) {
  return useQuery({
    queryKey: ["ruleCompose", ruleId],
    queryFn: () =>
      offerService.getRuleCompose(ruleId),
    enabled: !!ruleId,
  });
}

export function useGetAttributes(parentCode: string) {
  return useQuery({
    queryKey: ["attributes", parentCode],
    queryFn: () =>
      offerService.getAttributes(parentCode),
    enabled: !!parentCode,
  });
}

export function useGetDataTypes() {
  return useQuery({
    queryKey: ["datatypes"],
    queryFn: offerService.getDataTypes,
  });
}

export function useCreateRuleValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: offerService.createRuleValue,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rules"],
      });
    },
  });
}

export function useUpdateRuleValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      objverid,
      data,
    }: {
      objverid: string;
      data: AddOfferForm;
    }) =>
      offerService.updateRuleValue(
        objverid,
        data
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rules"],
      });
    },
  });
}

export function useToggleRuleValueStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      objverid,
      offId,
    }: {
      objverid: string;
      offId: string;
    }) =>
      offerService.toggleRuleValueStatus(
        objverid,
        offId
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rules"],
      });
    },
  });
}

export function useCodeManagerValues(name?: number, types?: string, parentType?: string) {
  return useQuery({
    queryKey: ["codes", name, types, parentType],
    queryFn: () =>
      offerService.getCodeFilterApi(name, types, parentType),
  });
}